// components/TableMovimientos.jsx

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Spinner,
  Table,
} from "react-bootstrap";

import {
  getMovimientos,
  deleteMovimiento,
  getMovimientoById,
} from "../helpers/rutaMovimientos";

import ModalMovimiento from "./ModalMovimiento";

import "../css/tablecomandas.css";
import "../css/movimientos.css";

const REGISTROS_POR_PAGINA = 12;
const PAGINAS_VISIBLES = 4;

const anchoDestino = {
  minWidth: 260,
  width: 260,
  whiteSpace: "normal",
  wordBreak: "break-word",
};

/* =========================================================
   HELPERS EXISTENTES
========================================================= */

const S = (v) =>
  typeof v === "string"
    ? v
    : v == null
    ? ""
    : String(v);

const isObj = (v) =>
  v &&
  typeof v === "object" &&
  !Array.isArray(v);

const fmtFecha = (d) =>
  d
    ? new Date(
        d
      ).toLocaleDateString(
        "es-AR",
        {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }
      )
    : "";

const fmtMoneda = (n) =>
  typeof n === "number"
    ? n.toLocaleString(
        "es-AR",
        {
          style: "currency",
          currency: "ARS",
          minimumFractionDigits: 2,
        }
      )
    : "-";

const ordenarPorMovimientoDesc = (
  lista = []
) =>
  [...lista].sort(
    (a, b) => {
      const nroA =
        Number(
          a?.codmov
        ) || 0;

      const nroB =
        Number(
          b?.codmov
        ) || 0;

      return (
        nroB -
        nroA
      );
    }
  );

/* =========================================================
   COMPONENTE
========================================================= */

const TableMovimientos = () => {
  const [
    rows,
    setRows,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    movimiento,
    setMovimiento,
  ] = useState(null);

  const [
    show,
    setShow,
  ] = useState(false);

  const [
    busqueda,
    setBusqueda,
  ] = useState("");

  const [
    pagina,
    setPagina,
  ] = useState(1);

  /*
    Evita doble fetch
    en React 18 StrictMode.
  */

  const fetchedRef =
    useRef(false);

  useEffect(() => {
    if (
      fetchedRef.current
    ) {
      return;
    }

    fetchedRef.current =
      true;

    consultaMovimientos();
  }, []);

  /* =======================================================
     PARSEO
  ======================================================= */

  const parseLista = (
    datos
  ) => {
    if (!datos) {
      return [];
    }

    if (
      Array.isArray(
        datos.movimientos
      )
    ) {
      return datos.movimientos;
    }

    if (
      Array.isArray(
        datos.items
      )
    ) {
      return datos.items;
    }

    if (
      Array.isArray(datos)
    ) {
      return datos;
    }

    return [];
  };

  const isHydrated = (
    list
  ) => {
    const m =
      list?.[0];

    if (!m) {
      return false;
    }

    return [
      m.caja,
      m.cuentaBancaria,
      m.gasto,
      m.comanda,
      m.camion,
      m.chofer,
    ].some(
      (x) =>
        x &&
        typeof x ===
          "object"
    );
  };

  /* =======================================================
     CONSULTA
     CONSERVAMOS LA LÓGICA ACTUAL
  ======================================================= */

  const consultaMovimientos =
    async () => {
      try {
        setLoading(true);

        const datos =
          await getMovimientos({
            full: 1,
          });

        const list =
          ordenarPorMovimientoDesc(
            parseLista(
              datos
            ) || []
          );

        setRows(list);

        /*
          Si ya vino hidratado,
          terminamos.
        */

        if (
          isHydrated(
            list
          )
        ) {
          setLoading(false);

          return;
        }

        /*
          Si no vino hidratado,
          mantenemos el mismo
          mecanismo existente.
        */

        await enrichFirstPage(
          list
        );

        setLoading(false);
      } catch (e) {
        console.error(
          "[MOVIMIENTOS] GET error:",
          e
        );

        setRows([]);

        setLoading(false);
      }
    };

  /* =======================================================
     HIDRATACIÓN EXISTENTE
  ======================================================= */

  const VISIBLE_TO_ENRICH =
    60;

  const CONCURRENCY =
    16;

  const KEYS = [
    "caja",
    "cuentaBancaria",
    "gasto",
    "comanda",
    "camion",
    "chofer",
  ];

  const needsEnrich = (
    m
  ) =>
    KEYS.some(
      (k) =>
        m[k] &&
        typeof m[k] ===
          "string"
    );

  const enrichFirstPage =
    async (list) => {
      const target =
        list
          .slice(
            0,
            VISIBLE_TO_ENRICH
          )
          .filter(
            needsEnrich
          );

      if (
        target.length ===
        0
      ) {
        return;
      }

      let idx = 0;

      const updated =
        [...list];

      const worker =
        async () => {
          while (
            idx <
            target.length
          ) {
            const current =
              target[idx++];

            try {
              const resp =
                await getMovimientoById(
                  current._id
                );

              if (
                resp?.ok &&
                resp.movimiento
              ) {
                const det =
                  resp.movimiento;

                const i =
                  updated.findIndex(
                    (x) =>
                      x._id ===
                      current._id
                  );

                if (
                  i >= 0
                ) {
                  const merged = {
                    ...updated[i],
                  };

                  KEYS.forEach(
                    (k) => {
                      if (
                        det[k]
                      ) {
                        merged[k] =
                          det[k];
                      }
                    }
                  );

                  updated[i] =
                    merged;
                }
              }
            } catch {
              // silencioso
            }
          }
        };

      await Promise.all(
        Array.from(
          {
            length:
              Math.min(
                CONCURRENCY,
                target.length
              ),
          },
          () => worker()
        )
      );

      setRows(
        updated
      );
    };

  /* =======================================================
     ACCIONES EXISTENTES
  ======================================================= */

  const handleClose =
    () => {
      setShow(false);

      consultaMovimientos();
    };

  const handleShow =
    () => setShow(true);

  const modificaMovimiento =
    async (id) => {
      try {
        const resp =
          await getMovimientoById(
            id
          );

        if (
          resp?.ok
        ) {
          setMovimiento(
            resp.movimiento
          );

          handleShow();
        } else {
          alert(
            resp?.error ||
              "No se pudo leer el movimiento"
          );
        }
      } catch (e) {
        alert(
          "No se pudo leer el movimiento"
        );
      }
    };

  const eliminarMovimiento =
    async (id) => {
      if (
        !window.confirm(
          "¿Está seguro que desea borrar el movimiento?"
        )
      ) {
        return;
      }

      try {
        const resp =
          await deleteMovimiento(
            id
          );

        if (
          resp?.ok
        ) {
          consultaMovimientos();
        } else {
          alert(
            resp?.error ||
              "No se pudo borrar el movimiento"
          );
        }
      } catch {
        alert(
          "No se pudo borrar el movimiento"
        );
      }
    };

  /* =======================================================
     CELDAS
     SOLO DATOS DE DB
  ======================================================= */

  const renderDestino = (
    mov
  ) => {
    const medio =
      S(
        mov.medio
      ).toUpperCase();

    if (
      medio ===
      "EFECTIVO"
    ) {
      return isObj(
        mov.caja
      )
        ? mov.caja
            .descripcion ||
            mov.caja
              .nombre ||
            mov.caja
              .codcaj ||
            "EFECTIVO"
        : "EFECTIVO (—)";
    }

    if (
      medio ===
      "TRANSFERENCIA"
    ) {
      const c =
        isObj(
          mov.cuentaBancaria
        )
          ? mov.cuentaBancaria
          : null;

      return c
        ? c.alias ||
            [
              c.banco,
              c.numero,
            ]
              .filter(
                Boolean
              )
              .join(" ")
        : "—";
    }

    if (
      medio ===
        "CHEQUE" ||
      medio ===
        "ICHEQ"
    ) {
      return "—";
    }

    return "—";
  };

  const renderGasto = (
    mov
  ) => {
    if (
      isObj(
        mov.gasto
      )
    ) {
      const tipo =
        mov.gasto.tipo ||
        mov.gasto
          .categoria ||
        mov.gasto.rubro ||
        null;

      const nombre =
        S(
          mov.gasto.gasto
        ).trim();

      return (
        <>
          <div
            style={{
              fontWeight:
                600,
            }}
          >
            {tipo ||
              nombre ||
              "—"}
          </div>

          {tipo &&
            nombre && (
              <small className="text-muted">
                {nombre}
              </small>
            )}
        </>
      );
    }

    return "—";
  };

  const renderViaje = (
    mov
  ) => {
    if (
      isObj(
        mov.comanda
      )
    ) {
      const nv =
        mov.comanda
          .nroviaje ||
        mov.comanda
          .codcom;

      return nv
        ? ` ${nv}`
        : "—";
    }

    return "—";
  };

  const renderCamion = (
    mov
  ) =>
    isObj(
      mov.camion
    )
      ? mov.camion
          .camion ||
        mov.camion
          .patente ||
        "—"
      : "—";

  const renderChofer = (
    mov
  ) =>
    isObj(
      mov.chofer
    )
      ? mov.chofer
          .nombre ||
        "—"
      : "—";

  /* =======================================================
     BÚSQUEDA
  ======================================================= */

  const movimientosActivos =
    useMemo(
      () =>
        (
          rows || []
        ).filter(
          (mov) =>
            mov?.activo !==
            false
        ),
      [rows]
    );

  const movimientosFiltrados =
    useMemo(() => {
      const texto =
        busqueda
          .trim()
          .toLowerCase();

      if (!texto) {
        return movimientosActivos;
      }

      return movimientosActivos.filter(
        (mov) => {
          const caja =
            isObj(
              mov.caja
            )
              ? [
                  mov.caja
                    .descripcion,
                  mov.caja
                    .nombre,
                  mov.caja
                    .codcaj,
                ]
                  .filter(
                    Boolean
                  )
                  .join(" ")
              : "";

          const banco =
            isObj(
              mov.cuentaBancaria
            )
              ? [
                  mov
                    .cuentaBancaria
                    .alias,
                  mov
                    .cuentaBancaria
                    .banco,
                  mov
                    .cuentaBancaria
                    .numero,
                ]
                  .filter(
                    Boolean
                  )
                  .join(" ")
              : "";

          const gasto =
            isObj(
              mov.gasto
            )
              ? [
                  mov.gasto
                    .gasto,
                  mov.gasto
                    .tipo,
                  mov.gasto
                    .categoria,
                  mov.gasto
                    .rubro,
                ]
                  .filter(
                    Boolean
                  )
                  .join(" ")
              : "";

          const viaje =
            isObj(
              mov.comanda
            )
              ? [
                  mov.comanda
                    .nroviaje,
                  mov.comanda
                    .codcom,
                ]
                  .filter(
                    Boolean
                  )
                  .join(" ")
              : "";

          const camion =
            isObj(
              mov.camion
            )
              ? [
                  mov.camion
                    .camion,
                  mov.camion
                    .patente,
                ]
                  .filter(
                    Boolean
                  )
                  .join(" ")
              : "";

          const chofer =
            isObj(
              mov.chofer
            )
              ? mov.chofer
                  .nombre ||
                ""
              : "";

          const contenido =
            [
              mov.codmov,
              fmtFecha(
                mov.fecha
              ),
              mov.tipo,
              mov.medio,
              mov.monto,
              mov.descripcion,
              mov.libre,
              caja,
              banco,
              gasto,
              viaje,
              camion,
              chofer,
            ]
              .filter(
                (x) =>
                  x !==
                    null &&
                  x !==
                    undefined
              )
              .join(" ")
              .toLowerCase();

          return contenido.includes(
            texto
          );
        }
      );
    }, [
      movimientosActivos,
      busqueda,
    ]);

  useEffect(() => {
    setPagina(1);
  }, [busqueda]);

  /* =======================================================
     PAGINACIÓN
  ======================================================= */

  const totalPaginas =
    Math.max(
      1,
      Math.ceil(
        movimientosFiltrados.length /
          REGISTROS_POR_PAGINA
      )
    );

  useEffect(() => {
    if (
      pagina >
      totalPaginas
    ) {
      setPagina(
        totalPaginas
      );
    }
  }, [
    pagina,
    totalPaginas,
  ]);

  const inicio =
    (
      pagina -
      1
    ) *
    REGISTROS_POR_PAGINA;

  const fin =
    inicio +
    REGISTROS_POR_PAGINA;

  const movimientosPagina =
    movimientosFiltrados.slice(
      inicio,
      fin
    );

  const paginas =
    useMemo(() => {
      let primera =
        pagina - 1;

      if (
        primera <
        1
      ) {
        primera = 1;
      }

      let ultima =
        primera +
        PAGINAS_VISIBLES -
        1;

      if (
        ultima >
        totalPaginas
      ) {
        ultima =
          totalPaginas;

        primera =
          Math.max(
            1,
            ultima -
              PAGINAS_VISIBLES +
              1
          );
      }

      const resultado =
        [];

      for (
        let i = primera;
        i <= ultima;
        i++
      ) {
        resultado.push(
          i
        );
      }

      return resultado;
    }, [
      pagina,
      totalPaginas,
    ]);

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div className="mov2-card">

      {loading ? (
        <div className="mov2-loading">
          <Spinner
            animation="border"
            size="sm"
          />

          <span>
            Cargando movimientos...
          </span>
        </div>
      ) : (
        <>
          {/* TOOLBAR */}

          <div className="mov2-toolbar">
            <div className="mov2-toolbar-title">
              <h4>
                Movimientos registrados
              </h4>

              <span>
                {
                  movimientosFiltrados.length
                }{" "}
                movimientos activos
              </span>
            </div>

            <div className="mov2-search">
              <label>
                Buscar
              </label>

              <input
                type="text"
                className="form-control"
                placeholder="Nro, descripción, viaje..."
                value={
                  busqueda
                }
                onChange={(
                  e
                ) =>
                  setBusqueda(
                    e.target
                      .value
                  )
                }
              />

              {busqueda && (
                <button
                  type="button"
                  className="mov2-clear"
                  onClick={() =>
                    setBusqueda(
                      ""
                    )
                  }
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {/* INFO */}

          <div className="mov2-info">
            <span>
              Mostrando{" "}
              <strong>
                {movimientosFiltrados.length
                  ? inicio +
                    1
                  : 0}
              </strong>{" "}
              a{" "}
              <strong>
                {Math.min(
                  fin,
                  movimientosFiltrados.length
                )}
              </strong>{" "}
              de{" "}
              <strong>
                {
                  movimientosFiltrados.length
                }
              </strong>
            </span>

            <span>
              Página{" "}
              <strong>
                {pagina}
              </strong>{" "}
              de{" "}
              <strong>
                {
                  totalPaginas
                }
              </strong>
            </span>
          </div>

          {/* TABLA */}

          <div className="mov2-table-scroll">
            <Table
              striped
              bordered
              hover
              className="table-container mov2-table"
            >
              <thead>
                <tr>
                  <th>
                    Nro
                  </th>

                  <th>
                    Fecha
                  </th>

                  <th>
                    Tipo
                  </th>

                  <th>
                    Medio
                  </th>

                  <th>
                    Monto
                  </th>

                  <th>
                    Descripción
                  </th>

                  <th>
                    Destino
                  </th>

                  <th>
                    Gasto
                  </th>

                  <th>
                    Viaje
                  </th>

                  <th>
                    Camión
                  </th>

                  <th>
                    Chofer
                  </th>

                  <th>
                    Libre
                  </th>
                </tr>
              </thead>

              <tbody>
                {movimientosPagina.length ===
                0 ? (
                  <tr>
                    <td
                      colSpan={12}
                      className="mov2-empty"
                    >
                      Sin movimientos
                      para mostrar.
                    </td>
                  </tr>
                ) : (
                  movimientosPagina.map(
                    (
                      mov,
                      idx
                    ) => (
                      <tr
                        key={
                          mov._id ||
                          idx
                        }
                      >
                        <td>
                          {mov.codmov ??
                            "-"}
                        </td>

                        <td>
                          {fmtFecha(
                            mov.fecha
                          )}
                        </td>

                        <td>
                          <span
                            className={`mov2-type ${
                              String(
                                mov.tipo ||
                                  ""
                              ).toLowerCase() ===
                              "ingreso"
                                ? "ingreso"
                                : String(
                                    mov.tipo ||
                                      ""
                                  ).toLowerCase() ===
                                  "egreso"
                                ? "egreso"
                                : ""
                            }`}
                          >
                            {mov.tipo ||
                              "-"}
                          </span>
                        </td>

                        <td>
                          {mov.medio ||
                            "-"}
                        </td>

                        <td className="mov2-money">
                          {fmtMoneda(
                            Number(
                              mov.monto ||
                                0
                            )
                          )}
                        </td>

                        <td
                          style={
                            anchoDestino
                          }
                        >
                          {S(
                            mov.descripcion
                          ) ||
                            "-"}
                        </td>

                        <td
                          style={
                            anchoDestino
                          }
                        >
                          {renderDestino(
                            mov
                          )}
                        </td>

                        <td>
                          {renderGasto(
                            mov
                          )}
                        </td>

                        <td>
                          {renderViaje(
                            mov
                          )}
                        </td>

                        <td>
                          {renderCamion(
                            mov
                          )}
                        </td>

                        <td>
                          {renderChofer(
                            mov
                          )}
                        </td>

                        <td>
                          {mov.libre ||
                            "-"}
                        </td>
                      </tr>
                    )
                  )
                )}
              </tbody>
            </Table>
          </div>

          {/* PAGINACIÓN */}

          <div className="mov2-pagination">
            <span className="mov2-page-size">
              12 movimientos por página
            </span>

            <div className="mov2-pagination-buttons">
              <button
                type="button"
                disabled={
                  pagina ===
                  1
                }
                onClick={() =>
                  setPagina(
                    1
                  )
                }
              >
                «
              </button>

              <button
                type="button"
                disabled={
                  pagina ===
                  1
                }
                onClick={() =>
                  setPagina(
                    pagina -
                      1
                  )
                }
              >
                ‹
              </button>

              {paginas.map(
                (numero) => (
                  <button
                    type="button"
                    key={
                      numero
                    }
                    className={
                      numero ===
                      pagina
                        ? "active"
                        : ""
                    }
                    onClick={() =>
                      setPagina(
                        numero
                      )
                    }
                  >
                    {numero}
                  </button>
                )
              )}

              <button
                type="button"
                disabled={
                  pagina ===
                  totalPaginas
                }
                onClick={() =>
                  setPagina(
                    pagina +
                      1
                  )
                }
              >
                ›
              </button>

              <button
                type="button"
                disabled={
                  pagina ===
                  totalPaginas
                }
                onClick={() =>
                  setPagina(
                    totalPaginas
                  )
                }
              >
                »
              </button>
            </div>
          </div>

          <ModalMovimiento
            show={show}
            handleClose={
              handleClose
            }
            movimiento={
              movimiento
            }
          />
        </>
      )}
    </div>
  );
};

export default TableMovimientos;